import { useUserInfo } from "@/api/user";
import { PERMISSIONS } from "@/constants/permissions";
import {
  Building,
  CreditCard,
  Key,
  Mic,
  Plug,
  Settings,
  User,
  Users,
} from "lucide-react";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

// Account
import AudioSettings from "./Account/AudioSettings";
import MyConnections from "./Account/MyConnections";
import MySettings from "./Account/MySettings";

// Organization
import OrgGroups from "./Organization/OrgGroups";
import OrgSettings from "./Organization/OrgSettings";
import OrgSSO from "./Organization/OrgSSO";
import OrgUsers from "./Organization/OrgUsers";

// Settings Pages
import UsageBilling from "../../pages/settings/Account/UsageBilling";

import { Config, config } from "@/config";
import { PLANS } from "@/constants/plans";
import { ROUTES } from "@/constants/routes";
import { usePermissions } from "@/hooks/usePermissions";
import { Flags, flagValues } from "@/providers/LaunchDarklyProvider";
import { route } from "@/utils/route";
import { useFlags } from "launchdarkly-react-client-sdk";

export function getVersion(github: Config["github"]): string | null {
  if (github.refName) {
    return github.refName;
  }

  if (github.sha) {
    return github.sha.slice(0, 7);
  }

  return null;
}

const version = getVersion(config.github);

interface MenuItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  new?: boolean;
  requiredPermissions?: string[];
  requiredFlags?: string[];
  requiredProvider?: string;
  requiredPlans?: string[];
  component?: React.FC;
}

export function useAdminPanelNavigationItems() {
  const flags = useFlags<Flags>();
  const { data: userInfo } = useUserInfo();
  const userPermissions = usePermissions();

  const hasMenuItemPermission = (item: MenuItem | false) => {
    if (!item) {
      return false;
    }

    if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
      return true;
    }
    return item.requiredPermissions.every((permission) =>
      userPermissions.includes(permission),
    );
  };

  const hasMenuItemFlags = (item: MenuItem | false) => {
    if (!item) {
      return false;
    }

    if (!item.requiredFlags || item.requiredFlags.length === 0) {
      return true;
    }
    return item.requiredFlags.every((flag) => flags[flag as keyof Flags]);
  };

  const hasMenuItemPlans = (item: MenuItem | false) => {
    if (!item) {
      return false;
    }

    if (!item.requiredPlans || item.requiredPlans.length === 0) {
      return true;
    }

    return item.requiredPlans?.some(
      (plan) => userInfo?.subscription?.plan_name === plan,
    );
  };

  const hasMenuItemProvider = (item: MenuItem | false) => {
    if (!item) {
      return false;
    }

    if (!item.requiredProvider) {
      return true;
    }

    return userInfo?.user?.provider === item.requiredProvider;
  };

  const menuItems: MenuItem[] = [
    {
      path: route(ROUTES.SETTINGS.SETTING, { settingId: "my-settings" }),
      icon: <Settings size={18} />,
      label: "Account",
      component: MySettings,
    },
    {
      path: route(ROUTES.SETTINGS.SETTING, { settingId: "connectors" }),
      icon: <Plug size={18} />,
      label: "Connectors",
      component: MyConnections,
    },
    {
      path: ROUTES.SETTINGS.AUDIO,
      icon: <Mic size={18} />,
      label: "Microphone",
      component: AudioSettings,
      requiredFlags: [flagValues.audioTranscription],
    },
  ]
    .filter(hasMenuItemProvider)
    .filter(hasMenuItemPlans)
    .filter(hasMenuItemPermission)
    .filter(hasMenuItemFlags);

  const workspaceItems: MenuItem[] = [
    {
      path: ROUTES.SETTINGS.ORGANIZATION,
      icon: <Building size={18} />,
      label: "Organization",
      requiredPermissions: [PERMISSIONS.ORG_ADMIN.ALL],
      component: OrgSettings,
    },
    {
      path: route(ROUTES.SETTINGS.SETTING, { settingId: "users" }),
      icon: <User size={18} />,
      label: "Members",
      requiredPermissions: [PERMISSIONS.ORG_ADMIN.ALL],
      component: OrgUsers,
    },
    {
      path: route(ROUTES.SETTINGS.SETTING, { settingId: "groups" }),
      icon: <Users size={18} />,
      label: "Groups",
      requiredPermissions: [PERMISSIONS.ORG_ADMIN.ALL],
      component: OrgGroups,
    },
    {
      path: route(ROUTES.SETTINGS.SETTING, {
        settingId: "sso",
      }),
      icon: <Key size={18} />,
      label: "Single sign-on",
      requiredPermissions: [PERMISSIONS.ORG_ADMIN.ALL],
      requiredPlans: [PLANS.ENTERPRISE],
      component: OrgSSO,
    },
    {
      path: ROUTES.SETTINGS.USAGE_BILLING,
      icon: <CreditCard size={18} />,
      label: "Usage & Billing",
      requiredPermissions: [PERMISSIONS.ORG_ADMIN.ALL],
      requiredFlags: [flagValues.billing],
      component: UsageBilling,
    },
  ]
    .filter(hasMenuItemPermission)
    .filter(hasMenuItemFlags)
    .filter(hasMenuItemPlans);

  const allMenuItems = useMemo(() => {
    return [...menuItems, ...workspaceItems];
  }, [menuItems, workspaceItems]);

  return {
    menuItems,
    workspaceItems,
    allMenuItems,
  };
}

type AdminPanelSidebarProps = {
  onMenuItemClick: (item: MenuItem) => void;
};

export const AdminPanelSidebar = (props: AdminPanelSidebarProps) => {
  const { onMenuItemClick } = props;
  const { data: userInfo } = useUserInfo();
  const location = useLocation();

  const { menuItems, workspaceItems } = useAdminPanelNavigationItems();

  const renderMenuItem = (item: MenuItem) => {
    const isActive = location.pathname.startsWith(item.path);

    return (
      <li
        key={item.path}
        className={`flex items-center p-2 rounded-lg cursor-pointer text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600 ${
          isActive ? "bg-neutral-200 dark:bg-neutral-700" : ""
        }`}
        onClick={() => onMenuItemClick(item)}
      >
        {item.icon}
        <span className="ml-2">{item.label}</span>
        {item.new && (
          <span className="ml-2 text-xs bg-rust text-white px-1 rounded">
            New
          </span>
        )}
      </li>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-lg font-medium ">Account</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
            {userInfo?.user.email}
          </p>
          <ul>{menuItems.map(renderMenuItem)}</ul>
        </div>
        {workspaceItems.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-medium ">Organization</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
              {userInfo?.organization.name}
            </p>
            <ul>{workspaceItems.map(renderMenuItem)}</ul>
          </div>
        )}
      </div>
      {version && (
        <div className="shrink-0 pt-4">
          <p className="text-xs text-muted-foreground flex justify-start">
            {version}
          </p>
        </div>
      )}
    </div>
  );
};
